/**
 * useWebhookHeaderActions — per-entity-type header action for outbound
 * webhooks, mirroring `useMeetingHeaderActions` / `useEmailThreadHeaderActions`.
 *
 * Contributes a single "Enable"/"Disable" toggle reflecting `ahoy_webhook_enabled`.
 * For non-webhook entities every inner value short-circuits (no queries) and
 * the returned array is always the same empty reference.
 */

import { Zap } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { usePermissions } from '@/hooks/usePermissions';
import type { ResponsiveAction } from '@/lib/base/buttons';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

const EMPTY_ACTIONS: ResponsiveAction[] = [];

export function useWebhookHeaderActions(
  object: CrmObject | null
): ResponsiveAction[] {
  const isWebhook = object?.entity_type_name === 'ahoy_outbound_webhook';
  const { hasPermission } = usePermissions();
  const { user } = useAuthInfo();

  const orgId = isWebhook ? object.org_id : undefined;
  const entityId = isWebhook ? object.entity_id : undefined;
  const lastEventSeq = isWebhook ? object.last_event_seq : undefined;
  // D-9: endpoints are BORN ENABLED — a MISSING ahoy_webhook_enabled means
  // enabled; only an explicit 'false' is disabled (mirrors the backend's
  // resolve_endpoint gate). `=== 'true'` here would show a fresh endpoint as
  // "off" and offer "Enable" for an endpoint that is already live.
  const enabled =
    getPropertyValue<string>(
      isWebhook ? object.properties?.ahoy_webhook_enabled : undefined
    ) !== 'false';

  // `ahoy_webhook_enabled` is a normal CRM property write, gated server-side
  // by the same creator-scoped write grant every other webhook write uses —
  // there's no separate "who can toggle" check to mirror here. There's no
  // per-entity creator signal on the record payload without an extra query
  // (see record-meta-section.tsx's CreatorName, which derives it from a
  // separate timeline event fetch), so visibility here is gated on a coarser
  // proxy — write permission for the entity type — and a CRM permission
  // rejection from the submit below is surfaced through the toast error path
  // for anyone who passes this check but isn't actually the creator.
  const canToggle = hasPermission(
    'ahoy_outbound_webhook',
    'ahoy_property_updated'
  );

  const handleToggle = useCallback(() => {
    if (orgId == null || entityId == null || user?.id == null) {
      return;
    }
    const nextEnabled = !enabled;
    void submitEventsOptimistic({
      orgId,
      userId: user.id,
      entityTypeName: 'ahoy_outbound_webhook',
      primaryEntityId: entityId,
      fallbackSeq: lastEventSeq ?? 0,
      build: builder =>
        builder.propertyUpdated(
          entityId,
          'ahoy_outbound_webhook',
          'ahoy_webhook_enabled',
          nextEnabled ? 'true' : 'false'
        ),
      errorToast: {
        message: () => "We couldn't update the webhook. Try again.",
      },
    })
      .then(() => {
        toast.success(nextEnabled ? 'Webhook turned on' : 'Webhook turned off');
      })
      .catch(() => {
        // submitEventsOptimistic already showed the error toast above.
      });
  }, [orgId, entityId, enabled, user?.id, lastEventSeq]);

  return useMemo(() => {
    if (!isWebhook || !canToggle || orgId == null || entityId == null) {
      return EMPTY_ACTIONS;
    }
    return [
      {
        key: 'webhook-toggle-enabled',
        label: enabled ? 'Disable' : 'Enable',
        icon: Zap,
        onClick: handleToggle,
        placement: 'dropdown' as const,
      },
    ];
  }, [isWebhook, canToggle, orgId, entityId, enabled, handleToggle]);
}
